import * as ko from "knockout";

import pliCall, { PliCallPromise, PliCallResponse, PliCallErrorData, PliCallSignInHandlerSettings } from "../core/call";
import * as pliObservable from "../forms-knockoutcontrols";

import * as shared from "./signInShared";
import { SignInPageForgot } from "./signInPageForgot";
import { SignInPageMessage } from "./signInPageMessage";
import { SignInPageChangePassword } from "./signInPageChangePassword";


/**
 * Settings for "SignInPageMain.activate" method.
 */
export interface SignInPageMainActivationSettings extends PliCallSignInHandlerSettings {
    /**
     * Transition type: undefined => opening, +1 => slide from right, -1 => slide from left.
     * External callers should not be setting this value. +/-1 is only used
     * when using activation to transition to this page from another sign-in page.
     */
    transition?: number;

    /**
     * How many milliseconds after activation should the initial focus be set.
     * When not defined, defaults to 500 milliseconds (default page transition animation duration).
     * When <= 0, focus is set instantly.
     */
    focusDelay?: number;
}


/**
 * Information returned from "SignInPageMain.activate" method.
 */
export interface SignInPageMainActivationInfo {
    /**
     * Will switch to "true" (from "null") when sign-in is complete.
     * Compatible with PliCallSignInHandler return value (used to retry call).
     * If "remainActiveUntil" was specified during activation,
     * "done" will be signalled before "active".
     */
    done: ko.Observable<boolean>;

    /**
     * Will switch to "false" (from "true") when viewmodel deactivates (closes).
     * When undefined/null, sign-in was already activated.
     */
    active?: ko.Observable<boolean>;
}


/**
 * Main sign-in page (username + password).
 *
 * Tracks and validates username + password entered by user,
 * then attempts to sign-in with those credentials on the server.
 */
export class SignInPageMain {

    constructor(owner: shared.SignInPagesCollection, pageId: shared.SignInPageId) {
        this._owner = owner;
        this._pageId = pageId;

        ko.computed(this._autoDeactivate, this);
    }


    // -------------------------------------------------------------------------
    // Viewmodel properties
    // -------------------------------------------------------------------------

    /** Computed flag that will switch to true while busy indicator (spinner) should be displayed. */
    busy: ko.PureComputed<boolean> = ko.pureComputed(this._calculateBusy, this);

    /** Error message currently being displayed. */
    error: ko.Observable<string> = ko.observable("");

    /** Tracks visibility of server sign-in message. */
    messageVisible: ko.Observable<boolean> = ko.observable(false);

    /** Flag that tracks status of remember me checkbox. */
    rememberMe: ko.Observable<boolean> = ko.observable(true);

    /** Tracks sign-in name entered by user. */
    username: shared.TextBoxObservableWithFocus<string> =
        shared.createTextBoxObservableWithFocus("", null, pliObservable.behaviours.required);

    /** Tracks sign-in password entered by user. */
    password: shared.TextBoxObservableWithFocusAndVisibility<string> =
        shared.createTextBoxObservableWithFocusAndVisibility("", null, pliObservable.behaviours.required);

    /** Server response that caused main-page to be activated. */
    serverData: ko.Observable<any> = ko.observable(shared.emptyData);


    // -------------------------------------------------------------------------
    // Viewmodel actions
    // -------------------------------------------------------------------------

    /**
     * Transitions to this page + set initial focus.
     */
    activate(settings?: SignInPageMainActivationSettings): SignInPageMainActivationInfo {
        // validate settings
        let focusDelay = 500; // default, duration of page transition
        let deferClosingUntil: ko.Observable<boolean> = null;
        if (settings) {
            if (settings.focusDelay !== undefined) { focusDelay = settings.focusDelay; }
            if (settings.deferClosingUntil !== undefined) {
                deferClosingUntil = settings.deferClosingUntil;
                if (deferClosingUntil !== null && !ko.isObservable(deferClosingUntil)) { throw new Error("settings.remainActiveUntil parameter must be undefined/null or an observable"); }
            }
        }

        // set initial property values (if needed)
        let activatingNow = false;
        if (this._active()) { // when returning, only reset validation errors
            this.error("");
            this.username.setErrorMessage(null);
            this.password.reset(false, "", "");
        } else {
            activatingNow = true;
            this._owner.currentPageId(shared.SignInPageId.None); // hide any previously activated page
            this._call(null);
            this._done(null);
            this._active(true);
            this._deferClosingUntil(deferClosingUntil || null);
            this.error("");
            this.username.reset(false, "", "");
            this.password.reset(false, "", "");
            this.serverData((settings && settings.serverData) || shared.emptyData);

            this.messageVisible(!!(this.serverData().signInMessage));
        }

        // initiate page transition + set initial focus only when activating
        // (opening) or transitioning between pages; in other words, do not
        // do this if opening already open sign-in form, e.g. when multiple
        // async calls concurrently get "401: Unauthorized" response
        const transition = (settings && settings.transition);
        if (activatingNow || typeof transition === "number") {
            this._owner.currentPageId((transition || +1) * this._pageId);

            if (focusDelay > 0) {
                setTimeout(() => this.username.hasFocus(true), focusDelay);
            } else {
                this.username.hasFocus(true);
            }
        }

        // return observable information that will notify subscribers when
        // sign-in is done (unauthenticated call can be retried)
        // and deactivated (sign-in form can be closed)
        return {
            active: activatingNow ? this._active : null,
            done: this._done
        };
    }

    /**
     * Resets server message shown to the user.
     */
    clearMessage(): void {
        this.messageVisible(false);
        this.username.hasFocus(true);
    }

    /** Transitions to "Forgot Username" page. */
    goToForgottenUsernamePage(): void {
        if (this.busy()) { return; }

        const cannotSignInPage = this._owner.getPage<SignInPageForgot>(shared.SignInPageId.Forgot);
        cannotSignInPage.activate({ username: this.username(), serverData: this.serverData() });
    }

    /** Transitions to "Forgot Password" page. */
    goToForgottenPasswordPage(): void {
        if (this.busy()) { return; }

        const cannotSignInPage = this._owner.getPage<SignInPageForgot>(shared.SignInPageId.Forgot);
        cannotSignInPage.activate({ username: this.username(), serverData: this.serverData(), resetPassword: true });
    }

    /**
     * Validates fields + initiates sign-in server call ("Pli-Authentication-SignIn").
     */
    submit(): void {
        if (this.busy()) { return; }

        this.username.validate();
        this.password.validate();

        let errorMessage: string = null;
        if (!this.password.isValid() && !this.username.isValid()) {
            this.username.hasFocus(true);
            errorMessage = (this.serverData().passwordResetRequiresUsername ? "Missing username and password" : "Missing email and password");
        } else if (!this.username.isValid()) {
            this.username.hasFocus(true);
            errorMessage = (this.serverData().passwordResetRequiresUsername ? "Missing username" : "Missing email");
        } else if (!this.password.isValid()) {
            this.password.hasFocus(true);
            errorMessage = "Missing password";
        }
        if (errorMessage) {
            this.error(errorMessage + ".\nPlease try again.");
            return;
        }

        this.error("");
        this._call(pliCall({
            owner: this,
            name: "Pli-Authentication-SignIn",
            data: {
                username: this.username(),
                password: this.password(),
                rememberMe: this.rememberMe()
            }
        }));
        this._call().always(SignInPageMain._processResponse);
    }

    /**
     * Toggles value of "password.visible" + sets "password.hasFocus".
     */
    togglePasswordVisibility(): void {
        this.password.visible(!this.password.visible());
        this.password.hasFocus(true);
    }


    // -------------------------------------------------------------------------
    // Private
    // -------------------------------------------------------------------------

    /**
     * Automatically changes "_active" to false:
     * - when "_done" is set to "true" (if "_deferClosingUntil" is null)
     * - when "_done" is set to "true" and "_deferClosingUntil" is set to "true".
     *
     * Not using pureComputed because there are no explicit subscribers.
     * This computed is simply running in the background observing "_done"
     * and "_deferClosingUntil()", producing a side-effect
     * (changing "_active") when appropriate.
     */
    private _autoDeactivate(): void {
        if (this._done()) {
            const deferClosingUntil = this._deferClosingUntil();
            if (!deferClosingUntil || deferClosingUntil() === true) {
                this._deferClosingUntil(null);
                this._active(false);
            }
        }
    }

    /**
     * Determines when this viewmodel is busy.
     * (submission and page transitions are not allowed when busy)
     */
    private _calculateBusy(): boolean {
        if (this._call()) { return true; }
        if (this._done()) { return true; }
        if (Math.abs(this._owner.currentPageId()) > shared.SignInPageId.Main) { return true; }
        return false;
    }

    /**
     * Processes result of sign-in server call ("Pli-Authentication-SignIn").
     */
    private static _processResponse(response: PliCallResponse<any, SignInPageMain>): void {
        if (response.owner._call().xhr !== response.clientXhr) {
            console.warn("Ignoring response ", response);
            return;
        }

        let focus: ko.Observable<boolean> = null;
        if (response.isError) {
            // is this a recognized error?
            let recognizedError = false;
            if (response.clientXhr.status === 403) {
                if (response.result.changePassword) {
                    recognizedError = true;
                    const changePasswordPage = response.owner._owner.getPage<SignInPageChangePassword>(shared.SignInPageId.ChangePassword);
                    changePasswordPage.activate({
                        username: response.owner.username(),
                        serverData: response.result.changePassword
                    });
                } else if (response.result.lockedOut) {
                    recognizedError = true;
                    const lockedOutPage = response.owner._owner.getPage<SignInPageMessage>(shared.SignInPageId.LockedOut);
                    lockedOutPage.activate({
                        serverData: response.result.lockedOut
                    });
                }
            }

            // if not a recognized error, show it in UI
            if (!recognizedError) {
                const errorData = response.result as PliCallErrorData;
                response.owner.error(errorData.message + ".\nPlease try again.");

                focus = response.owner.username.hasFocus;
                if (errorData.validationErrors) {
                    let message: string;
                    message = errorData.validationErrors["password"];
                    if (message) { response.owner.password.setErrorMessage(message); focus = response.owner.password.hasFocus; }
                    message = errorData.validationErrors["username"];
                    if (message) { response.owner.username.setErrorMessage(message); focus = response.owner.username.hasFocus; }
                }
            }
        } else { // success response
            response.owner._done(true);
        }

        response.owner._call(null);
        if (focus) { focus(true); }
    }

    /**
     * Observable that will be set to "true" when this viewmodel is activated and to "false" when deactivated.
     * If "_deferClosingUntil" is set to an observable in "activate" method,
     * deactivation will be deferred until "true" is signalled by "_deferClosingUntil".
     */
    private _active: ko.Observable<boolean> = ko.observable(false);

    /** Reference to the current server call (sign-in request). */
    private _call: ko.Observable<PliCallPromise<any, SignInPageMain>> = ko.observable(null);

    /**
     * Observable that can be set in "activate" method (optional) that defers deactivation of this viewmodel.
     * For more information see comments for "_active" property and "_autoDeactivate" method.
     */
    private _deferClosingUntil: ko.Observable<ko.Observable<boolean>> = ko.observable(null); // readonly

    /**
     * Flag that will be switched to "true" when server sign-in is successfully completed.
     * (Set to "null" in "activate", to "true" in "_processResponse").
     */
    private _done: ko.Observable<boolean> = ko.observable(null);

    /** Collection that owns this page. Used for page transitions. */
    private _owner: shared.SignInPagesCollection;

    /** ID of the page that was instantiated with this viewmodel instance. */
    private _pageId: shared.SignInPageId; // readonly, initialized in constructor

}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/forms-signin/signInPageMain.ts